import { Component, Input, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { DataService } from '../../../services/data.service';
import { AlertConfirmService } from '../../../services/alert-confirm.service';
import { AuthService } from '../../../services/auth.service';
import { HttpClient, HttpEventType } from '@angular/common/http';

interface Subject {
  name: string;
  marksObtained: number | null;
  maxMarks: number | null;
}

interface UploadedDoc {
  name: string;
  label: string;
  file: File | null;
  fileName: string;
}

@Component({
  selector: 'app-application-form',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [DatePipe],
  templateUrl: './application-form.component.html',
  styleUrl: './application-form.component.scss',
})
export class ApplicationFormComponent {
  @Input() action: any;

  alertService = inject(AlertConfirmService);
  dataService = inject(DataService);
  datePipe = inject(DatePipe);
  currentDate: Date = new Date();
  editID: number = 0;

  submitted = false;
  submitSuccess = false;
  sameAsPermAddress = false;
  searchTerm: string = '';

  // A. Personal Details
  personal = {
    inq_code: '',
    app_code: '',
    studentName: '',
    gender: '',
    dateOfBirth: '',
    category: '',
    religion: '',
    nationality: 'Indian',
    aadhaarNumber: '',
    profile_photo: ''
  };

  // B. Parent Details
  father = {
    name: '',
    qualification: '',
    occupation: '',
    mobile: '',
    email: '',
    annualIncome: '',
  };

  mother = {
    name: '',
    qualification: '',
    occupation: '',
    mobile: '',
    email: '',
    annualIncome: '',
  };

  guardian = {
    name: '',
    relation: '',
    mobile: '',
    email: '',
    address: '',
  };

  // C. Address
  permanentAddress = {
    houseNo: '',
    street: '',
    city: '',
    state: '',
    pinCode: '',
    country: 'India',
  };

  correspondenceAddress = {
    houseNo: '',
    street: '',
    city: '',
    state: '',
    pinCode: '',
    country: 'India',
  };

  // D. Academic Details
  academics = {
    previousSchool: '',
    board: '',
    passingYear: '',
    percentage: null as number | null,
    tcNumber: '',
    subjects: []
  };

  subjects: Subject[] = [
    { name: '', marksObtained: null, maxMarks: 100 },
  ];

  // E. Additional Information
  additional = {
    transportRequired: false,
    hostelRequired: false,
    scholarshipCategory: '',
    sportsCategory: '',
    bloodGroup: '',
    medicalCondition: '',
  };

  // F. Documents
  documents: UploadedDoc[] = [
    { name: 'birthCertificate', label: 'Birth Certificate', file: null, fileName: '' },
    { name: 'aadhaarCard', label: 'Aadhaar Card', file: null, fileName: '' },
    { name: 'previousMarksheet', label: 'Previous Class Marksheet', file: null, fileName: '' },
    { name: 'tcCertificate', label: 'Transfer Certificate (TC)', file: null, fileName: '' },
    { name: 'casteCertificate', label: 'Caste/Category Certificate', file: null, fileName: '' },
    { name: 'passport', label: 'Passport Size Photograph', file: null, fileName: '' },
  ];

  // Declaration
  declaration = {
    accepted: false,
    place: '',
    date: new Date().toISOString().substring(0, 10),
  };

  readonly genderOptions = ['Male', 'Female', 'Other', 'Prefer not to say'];
  readonly categoryOptions = this.dataService.categories;
  readonly religionOptions = this.dataService.religions;
  readonly boardOptions = this.dataService.boards;
  readonly scholarshipOptions = ['None', 'Merit', 'Minority', 'SC/ST', 'OBC', 'Sports', 'Disability'];
  readonly sportsOptions = ['None', 'Cricket', 'Football', 'Basketball', 'Athletics', 'Swimming', 'Badminton', 'Tennis', 'Volleyball', 'Kabaddi', 'Other'];
  readonly bloodGroupOptions = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  readonly stateOptions = this.dataService.cityDataDD;
  districts1: any = null;
  districts2: any = null;

  readonly passingYears: number[] = (() => {
    const years = [];
    for (let y = new Date().getFullYear(); y >= 2000; y--) years.push(y);
    return years;
  })();

  get formProgress(): number {
    let filled = 0;
    const checks = [
      this.personal.studentName,
      this.personal.gender,
      this.personal.dateOfBirth,
      this.personal.category,
      this.personal.aadhaarNumber,
      this.father.name,
      this.mother.name,
      this.permanentAddress.city,
      this.permanentAddress.state,
      this.correspondenceAddress.city,
      this.academics.previousSchool,
      this.academics.board,
      this.academics.passingYear,
    ];
    checks.forEach(v => { if (v) filled++; });
    return Math.round((filled / checks.length) * 100);
  }

  user: any = null;

  constructor(private auth: AuthService, private http: HttpClient) {
    //
  }

  ngOnInit() {
    let erp_rem: any = localStorage.getItem("erp_rem");
    if(erp_rem) {
      erp_rem = JSON.parse(erp_rem);
      erp_rem = this.dataService.deCodeUser(erp_rem);
      this.user = erp_rem;
    }

    // Listen to changes continuously
    this.dataService.outerAction.subscribe((value: any) => {
      if (value) {
        if(value.component != 'application') return;

        this.action = value.action;

        if (value.action == 'addnew') {
          this.editID = 0;
          this.onReset(null);
          const numbers = '0123456789';
          const customCode = this.dataService.getUUID(6, numbers);
          this.personal.app_code = customCode;
        }
        if (value.action == 'edit') {
          this.editID = value.data.id;
          this.getEditData(value.data);
        }

        this.dataService.updateOuterAction(null);   //reset value
      }
    });
  }

  ngOnChanges() {
    if (this.action) {
      switch (this.action) {
        case 'addnew':
          // this.isReadonly = false;
          // this.frmDataSet();
          // this.resetForm();
          break;
        case 'modify':
          // this.isReadonly = false;
          // this.frmDataSet();
          break;
        case 'save':
          // this.doSave();
          // this.isReadonly = true;
          break;
        case 'update':
          // this.doUpdate();
          // this.isReadonly = true;
          break;
        case 'cancel':
          // this.isReadonly = true;
          // this.resetForm();
          break;
        default:
          break;
      }

      this.action = '';
    }
  }

  onprofile_photo(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) {
      const reader = new FileReader();
      reader.onload = e => {
				if(e.target?.result && e.total < 40000) {
					this.personal.profile_photo = e.target?.result as string;
				} else {
					this.personal.profile_photo = '';
					alert('Error! File size too large.');
				}			
			}
      reader.readAsDataURL(input.files[0]);
    }
  }

  getEditData(frmData: any) {
    if (frmData.std_info && typeof frmData.std_info == 'string') {
      frmData.std_info = JSON.parse(frmData.std_info);
    }

    if (frmData.std_info && frmData.std_info.additional) {
      if (typeof frmData.std_info.additional == 'string') {
        frmData.std_info.additional = JSON.parse(frmData.std_info.additional);
        frmData.additional = JSON.parse(frmData.std_info.additional);
      } else {
        frmData.std_info.additional = frmData.std_info.additional;
        frmData.additional = frmData.std_info.additional;
      }
    }

    if (frmData.f_info && typeof frmData.f_info == 'string') {
      frmData.f_info = JSON.parse(frmData.f_info);
    }

    if (frmData.m_info && typeof frmData.m_info == 'string') {
      frmData.m_info = JSON.parse(frmData.m_info);
    }

    if (frmData.g_info && typeof frmData.g_info == 'string') {
      frmData.g_info = JSON.parse(frmData.g_info);
    }

    if (frmData.additional && typeof frmData.additional == 'string') {
      frmData.additional = JSON.parse(frmData.additional);
    }

    if (frmData.declaration && typeof frmData.declaration == 'string') {
      frmData.declaration = JSON.parse(frmData.declaration);
    }

    if (frmData.academics && typeof frmData.academics == 'string') {
      frmData.academics = JSON.parse(frmData.academics);
    }

    if (frmData.subjects && typeof frmData.subjects == 'string') {
      frmData.subjects = JSON.parse(frmData.subjects);
    }

    if (frmData.academics && frmData.academics.subjects) {
      if (typeof frmData.academics.subjects == 'string') {
        frmData.subjects = JSON.parse(frmData.academics.subjects);
        frmData.academics.subjects = JSON.parse(frmData.academics.subjects);
      } else {
        frmData.subjects = frmData.academics.subjects;
        frmData.academics.subjects = frmData.academics.subjects;
      }
    } else {
      if(frmData.academics) {
        frmData.academics.subjects = [];
      }
    }

    if (frmData.addresses && typeof frmData.addresses == 'string') {
      frmData.addresses = this.dataService.str2json(frmData.addresses);
    }

    if (frmData.documents && typeof frmData.documents == 'string') {
      frmData.documents = this.dataService.str2json(frmData.documents);
    }

    
    const numbers = '0123456789';
    const customCode = this.dataService.getUUID(6, numbers);
    if(this.editID && this.editID > 0) {
      this.personal.inq_code = frmData.code || frmData.inq_code;
      this.personal.app_code = frmData.app_code || customCode;
    } else {
      this.personal.inq_code = frmData.inq_code || '';
      this.personal.app_code = customCode;
    }

    this.personal.studentName = frmData.iname || frmData.std_name;
    this.personal.dateOfBirth = frmData.dob?.substr(0, 10) || frmData.std_dob?.substr(0, 10);
    this.personal.gender = frmData.gender || frmData.std_info.gender;
    if (this.editID > 0) {
      this.personal.category = frmData.std_info.category;
      this.personal.religion = frmData.std_info.religion;
      // frmData.std_info.nationality: 'Indian',
      this.personal.category = frmData.std_info.category;
      this.personal.religion = frmData.std_info.religion;
      // frmData.std_info.nationality: 'Indian',
      this.personal.aadhaarNumber = frmData.std_info.aadhaarNumber;
      this.personal.profile_photo = frmData.profile_photo;
    }
    this.father.name = frmData.father || frmData.father_name;
    if (this.editID > 0) {
      this.father.qualification = frmData.f_info.qualification;
      this.father.occupation = frmData.f_info.occupation;
      this.father.mobile = frmData.f_info.mobile;
      this.father.email = frmData.f_info.email;
      this.father.annualIncome = frmData.f_info.annualIncome;
    }
    this.mother.name = frmData.mother || frmData.mother_name;
    if (this.editID > 0) {
      this.mother.qualification = frmData.m_info.qualification;
      this.mother.occupation = frmData.m_info.occupation;
      this.mother.mobile = frmData.m_info.mobile;
      this.mother.email = frmData.m_info.email;
      this.mother.annualIncome = frmData.m_info.annualIncome;
    }
    this.guardian.name = frmData.guardian || frmData.guardian_name;
    if (this.editID > 0) {
      this.guardian.relation = frmData.g_info.relation;
      this.guardian.mobile = frmData.g_info.mobile;
      this.guardian.email = frmData.g_info.email;
      this.guardian.address = frmData.g_info.address;

      this.permanentAddress = frmData.addresses[0];
      this.correspondenceAddress = frmData.addresses[1];

      if(this.permanentAddress.state) {
        this.districts1 = this.dataService.getDist(this.permanentAddress.state);
      }
      if(this.correspondenceAddress.state) {
        this.districts2 = this.dataService.getDist(this.correspondenceAddress.state);
      }

      this.academics = frmData.academics;
      this.subjects = frmData.academics.subjects || frmData.subjects || this.subjects;
      this.academics.subjects = frmData.academics.subjects || frmData.subjects || this.subjects;
      this.documents = frmData.documents;
      this.additional = frmData.additional || {};
      this.additional.transportRequired = frmData.req_transport;
      this.additional.hostelRequired = frmData.req_hostel;
      this.currentDate = frmData.reg_date.substr(0, 10);
      // frmData.is_active: true,
      this.declaration = frmData.declaration;
    }
  }

  addSubject(): void {
    this.subjects.push({ name: '', marksObtained: null, maxMarks: 100 });
  }

  removeSubject(index: number): void {
    if (this.subjects.length > 1) {
      this.subjects.splice(index, 1);
    }
  }

  onFileSelect(event: Event, doc: UploadedDoc): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      doc.file = input.files[0];
      doc.fileName = input.files[0].name;

      this.doUpload(doc.file, 'std_docs/' + this.personal.app_code);
    }
  }

  doUpload(selectedFile: any, file4: string) {
    // this.isUploading = true;
    const formData4Img = new FormData();
    formData4Img.append('file', selectedFile);
    // formData4Img.append('erp_key', this.user.erp_key);

    this.http.post(this.auth.apiUrl+'/upload?file4='+file4+'&erp_key='+this.user.erp_key, formData4Img,
      {
        reportProgress: true,
        observe: 'events'
      }
    ).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          // this.uploadProgress = Math.round(
          //   (100 * event.loaded) / (event.total || 1)
          // );
        } else if (event.type === HttpEventType.Response) {
          // this.uploadResponse = event.body;
          // this.isUploading = false;
        }
      },
      error: (error: any) => {
        // this.errorMessage = error.error?.message || 'Upload failed';
        // this.isUploading = false;
      }
    });
  }

  onSameAddressChange(): void {
    if (this.sameAsPermAddress) {
      this.correspondenceAddress = { ...this.permanentAddress };
    } else {
      this.correspondenceAddress = {
        houseNo: '',
        street: '',
        city: '',
        state: '',
        pinCode: '',
        country: 'India',
      };
    }
  }

  validateAadhaar(value: string): boolean {
    return /^\d{12}$/.test(value);
  }

  validateMobile(value: string): boolean {
    return /^[6-9]\d{9}$/.test(value);
  }

  validatePinCode(value: string): boolean {
    return /^\d{6}$/.test(value);
  }

  validateEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  getMaxDOB(): string {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 3);
    return d.toISOString().substring(0, 10);
  }

  getMinDOB(): string {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 30);
    return d.toISOString().substring(0, 10);
  }

  onSubmit(form: NgForm): void {
    this.submitted = true;
    // if (form.invalid || !this.declaration.accepted) {
    if (form.invalid) {
      const firstInvalid = document.querySelector('.is-invalid, .ng-invalid.ng-touched');
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    this.submitSuccess = true;
    setTimeout(() => {
      this.submitSuccess = false;
    }, 5000);

    const std_infoZ = {
      gender: this.personal.gender,
      category: this.personal.category,
      religion: this.personal.religion,
      nationality: 'Indian',
      aadhaarNumber: this.personal.aadhaarNumber,
      additional: this.additional
    };
    const f_infoZ = {
      qualification: this.father.qualification,
      occupation: this.father.occupation,
      mobile: this.father.mobile,
      email: this.father.email,
      annualIncome: this.father.annualIncome,
    };
    const m_infoZ = {
      qualification: this.mother.qualification,
      occupation: this.mother.occupation,
      mobile: this.mother.mobile,
      email: this.mother.email,
      annualIncome: this.mother.annualIncome,
    };
    const g_infoZ = {
      relation: this.guardian.relation,
      mobile: this.guardian.mobile,
      email: this.guardian.email,
      address: this.guardian.address,
    };

    let frmData: any = {
      inq_code: this.personal.inq_code,
      app_code: this.personal.app_code,
      std_name: this.personal.studentName,
      std_dob: this.personal.dateOfBirth,
      std_info: std_infoZ,
      father_name: this.father.name,
      f_info: f_infoZ,
      mother_name: this.mother.name,
      m_info: m_infoZ,
      guardian_name: this.guardian.name,
      g_info: g_infoZ,
      addresses: [this.permanentAddress, this.correspondenceAddress],
      academics: this.academics,
      documents: this.documents,
      req_transport: this.additional.transportRequired,
      req_hostel: this.additional.hostelRequired,
      reg_date: this.currentDate,
      is_active: true,
      declaration: this.declaration,
      profile_photo: this.personal.profile_photo
    }
    frmData.academics.subjects = this.subjects;

    if (this.editID == 0) {
      this.doSave(frmData);
    } else {
      this.doUpdate(frmData);
    }
  }

  onReset(form: NgForm | null): void {
    this.submitted = false;
    this.sameAsPermAddress = false;
    if(form) form?.resetForm();
    this.personal = { inq_code: '', app_code: '', studentName: '', gender: '', dateOfBirth: '', category: '', religion: '', nationality: 'Indian', aadhaarNumber: '', profile_photo: '' };
    this.father = { name: '', qualification: '', occupation: '', mobile: '', email: '', annualIncome: '' };
    this.mother = { name: '', qualification: '', occupation: '', mobile: '', email: '', annualIncome: '' };
    this.guardian = { name: '', relation: '', mobile: '', email: '', address: '' };
    this.permanentAddress = { houseNo: '', street: '', city: '', state: '', pinCode: '', country: 'India' };
    this.correspondenceAddress = { houseNo: '', street: '', city: '', state: '', pinCode: '', country: 'India' };
    this.academics = { previousSchool: '', board: '', passingYear: '', percentage: null, tcNumber: '', subjects: [] };
    this.subjects = [{ name: '', marksObtained: null, maxMarks: 100 }];
    this.additional = { transportRequired: false, hostelRequired: false, scholarshipCategory: '', sportsCategory: '', bloodGroup: '', medicalCondition: '' };
    this.documents.forEach(d => { d.file = null; d.fileName = ''; });
    this.declaration = { accepted: false, place: '', date: new Date().toISOString().substring(0, 10) };
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  doSave(frmData: any) {
    // this.loading = true;
    this.submitSuccess = true;
    this.editID = 0;

    this.dataService.addNewRecord('applications', frmData).subscribe({
      next: (response) => {
        // set activity log details
				this.dataService.setActivityLog('create', 'application', 'New application of "'+this.personal.studentName+'" added.');
        this.submitSuccess = false;
        // console.log('Response received in component:', response);
        // this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        // this.loading = false;
      }
    });
  }

  doUpdate(frmData: any) {
    // this.loading = true;
    this.submitSuccess = true;
    const myid = this.editID || frmData.id;
    delete frmData.id;
    this.editID = 0;

    this.dataService.updateRecord('applications', myid, frmData).subscribe({
      next: (response) => {
        // set activity log details
				this.dataService.setActivityLog('update', 'application', 'Application of "'+this.personal.studentName+'" details updated.');
        this.submitSuccess = false;
        this.dataService.updateOuterAction({
          component: 'application',
          action: 'reload', data: null
        });

        // console.log('Response received in component:', response);
        // this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        // this.loading = false;
      }
    });
  }

  onSearchChange(): void {
    // alert(this.searchTerm);
    // this.loading = true;
    this.dataService.getSingleRecord('inquiries', this.searchTerm).then((res: any) => {
      // this.allData = res;
      // this.orgData = Object.assign([], res);
      // this.loading = false;
      if (res) {
        // console.log(JSON.stringify(res));
        this.getEditData(res);
        // this.alertService.alert(
        //   'Data Search!',
        //   'Record found & fetched. Available details auto filled in the form.',
        //   'success'
        // );
      } else {
        this.alertService.alert(
          'Error!',
          'Record not found.',
          'danger'
        );
      }
    });
  }
}